import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';

import {
  AI_CHAT_MESSAGE_FEEDBACK,
  AI_CHAT_MESSAGE_FEEDBACK_UPSERT,
} from '@atom/graph/ai';
import {
  AIChatMessageFeedback,
  AIChatMessageFeedbackConnection,
  AIChatMessageFeedbackConnectionInput,
  AIChatMessageFeedbackSentiment,
  AIChatMessageFeedbackUpsertInput,
} from '@atom/types/ai';

type FeedbackMap = Record<string, AIChatMessageFeedback>;

export type UseFeedbackResponse = {
  feedback: FeedbackMap;
  loading: boolean;
  error?: ApolloError | null;
  onFeedbackGiven: (
    messageId: string,
    sentiment: AIChatMessageFeedbackSentiment,
    comment?: string,
  ) => Promise<void>;
};

export const useFeedback = (threadId: string) => {
  const [feedback, setFeedback] = useState<FeedbackMap>({});
  // use state for loading instead of useQuery since we need to wait
  // until onCompleted has been called and feedback state is set
  const [loading, setLoading] = useState(false);

  const createFeedbackMap = useCallback((fb: AIChatMessageFeedback[]) => {
    return fb.reduce((acc, value): Record<string, AIChatMessageFeedback> => {
      return {
        ...acc,
        [value.messageId]: value,
      };
    }, {});
  }, []);

  const [getFeedback, { error }] = useLazyQuery<
    { aiChatMessageFeedback: AIChatMessageFeedbackConnection },
    { input: AIChatMessageFeedbackConnectionInput }
  >(AI_CHAT_MESSAGE_FEEDBACK, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setFeedback(createFeedbackMap(data.aiChatMessageFeedback.feedback));
      setLoading(false);
    },
  });

  const [upsertFeedback] = useMutation<
    {
      aiChatMessageFeedbackUpsert: AIChatMessageFeedback;
    },
    {
      input: AIChatMessageFeedbackUpsertInput;
    }
  >(AI_CHAT_MESSAGE_FEEDBACK_UPSERT);

  useEffect(() => {
    if (threadId) {
      setLoading(true);
      getFeedback({ variables: { input: { threadId } } });
    }
  }, [threadId]);

  const onFeedbackGiven = useCallback(
    async (
      messageId: string,
      sentiment: AIChatMessageFeedbackSentiment,
      comment?: string,
    ) => {
      const result = await upsertFeedback({
        variables: {
          input: {
            threadId,
            messageId,
            sentiment,
            comment,
          },
        },
      });

      const updated = result.data?.aiChatMessageFeedbackUpsert;

      setFeedback(current => {
        return {
          ...current,
          [updated.id]: updated,
        };
      });
    },
    [threadId],
  );

  return useMemo<UseFeedbackResponse>(
    () => ({
      feedback,
      loading,
      error,
      onFeedbackGiven,
    }),
    [feedback, loading, error, onFeedbackGiven],
  );
};
